import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  Target,
  TrendingUp,
  Award,
  Layers,
  Activity,
  Crown,
  CheckCircle2,
} from "lucide-react";
import ClientsMarquee from "../components/ClientsMarquee";
import { SectionHeader, Stat } from "../components/Section";
import { TRAINING_PROGRAMS, CONSULTING_SERVICES, STATS, TESTIMONIALS } from "../data/site";

const ICONS = {
  "yellow-belt": Target,
  "green-belt": TrendingUp,
  "black-belt": Award,
  "lean-foundations": Layers,
  "lean-practitioner": Activity,
  "lean-leadership": Crown,
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1742070220779-a28adaa80462?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxob25nJTIwa29uZyUyMHNreWxpbmUlMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzc3NDMwNjQ3fDA&ixlib=rb-4.1.0&q=85";

const Hero = () => (
  <section data-testid="hero" className="relative overflow-hidden border-b border-black/10">
    <div className="absolute inset-0 -z-10">
      <img src={HERO_IMG} alt="Hong Kong skyline" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-white/92" />
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="grain" />
    </div>

    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-28 lg:pt-36 lg:pb-40">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#E63946]" />
            <span className="font-mono-tech text-[11px] tracking-widest text-zinc-600 uppercase">
              HK · APAC
            </span>
          </div>
        </div>

        <div className="lg:col-span-10">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs tracking-[0.24em] uppercase text-[#E63946] font-semibold mb-6"
          >
            Lean Six Sigma · Operational Excellence · Turnaround
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-5xl sm:text-6xl lg:text-[88px] font-black tracking-[-0.04em] leading-[0.92] text-black"
          >
            Precision in process.{" "}
            <span className="text-[#E63946]">Excellence</span> in execution.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="mt-10 max-w-2xl text-lg lg:text-xl text-zinc-600 leading-relaxed"
          >
            Based in Hong Kong, we certify Lean Six Sigma practitioners and turn around
            under-performing operations across the Asia-Pacific. Twenty years. Over five thousand
            certifications. Hundreds of millions in verified impact.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 flex flex-wrap gap-3"
          >
            <Link
              to="/training"
              data-testid="hero-cta-training"
              className="group inline-flex items-center gap-2 bg-[#E63946] text-white px-7 py-4 text-sm font-semibold tracking-wide hover:bg-[#D32F2F] transition-colors"
            >
              Explore certifications
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.75} />
            </Link>
            <Link
              to="/consulting"
              data-testid="hero-cta-consulting"
              className="group inline-flex items-center gap-2 border border-black text-black px-7 py-4 text-sm font-semibold tracking-wide hover:bg-black hover:text-white transition-colors"
            >
              Consulting services
              <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
          </motion.div>

          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-10 max-w-4xl">
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="font-display text-3xl lg:text-4xl font-black text-black tracking-tighter">
                  {s.value}
                </p>
                <p className="mt-2 text-[11px] tracking-[0.18em] uppercase text-zinc-500">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TrainingGrid = () => (
  <section data-testid="home-training" className="border-b border-black/10">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
        <SectionHeader
          overline="01 · Certifications"
          title={<>Six programmes. <span className="text-[#E63946]">One standard.</span></>}
          kicker="Whether you're starting your first improvement project or building an enterprise programme, our certifications meet the international body of knowledge — taught by practitioners who've done the work."
        />
        <Link
          to="/training"
          data-testid="training-grid-cta"
          className="link-underline self-start lg:self-end inline-flex items-center gap-2 text-sm font-semibold text-black"
        >
          View all programmes <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10">
        {TRAINING_PROGRAMS.map((p) => {
          const Icon = ICONS[p.id] || Target;
          return (
            <Link
              key={p.id}
              to="/training"
              data-testid={`home-training-${p.id}`}
              className="group bg-white p-10 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-300 flex flex-col"
            >
              <div className="flex items-center justify-between mb-10">
                <Icon className="w-7 h-7 text-[#E63946]" strokeWidth={1.5} />
                <span className="font-mono-tech text-[11px] tracking-widest text-zinc-500 group-hover:text-zinc-400">
                  {p.code}
                </span>
              </div>
              <p className="text-[11px] tracking-[0.22em] uppercase text-zinc-500 group-hover:text-zinc-400 mb-3">
                {p.level}
              </p>
              <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">
                {p.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-zinc-600 group-hover:text-zinc-300 flex-1">
                {p.description.slice(0, 140)}…
              </p>
              <div className="mt-8 pt-6 border-t border-black/10 group-hover:border-white/10 flex items-center justify-between text-xs">
                <span className="font-mono-tech text-zinc-500 group-hover:text-zinc-400">
                  {p.duration}
                </span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.75} />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

const ConsultingTiles = () => (
  <section data-testid="home-consulting" className="bg-[#F7F7F8] border-b border-black/10">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
      <SectionHeader
        overline="02 · Consulting"
        title={<>Four engagements for the moments that matter.</>}
        kicker="When the brief is bigger than a course — when results need to land in the P&L this quarter — we work shoulder-to-shoulder with leadership teams."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
        {CONSULTING_SERVICES.map((s, idx) => (
          <Link
            key={s.id}
            to={`/consulting#${s.id}`}
            data-testid={`home-consulting-${s.id}`}
            className="group bg-white p-10 lg:p-10 hover:bg-[#E63946] hover:text-white transition-colors duration-300 flex flex-col"
          >
            <p className="font-mono-tech text-[11px] tracking-widest text-zinc-500 group-hover:text-white/80 mb-6">
              0{idx + 1} / 04
            </p>
            <h3 className="font-display text-2xl lg:text-3xl font-black tracking-tighter">
              {s.title}
            </h3>
            <p className="mt-5 text-sm tracking-wide text-zinc-500 group-hover:text-white/80 italic">
              {s.tagline}
            </p>
            <p className="mt-8 text-sm leading-relaxed text-zinc-600 group-hover:text-white/90 flex-1">
              {s.summary}
            </p>
            <div className="mt-10 inline-flex items-center gap-2 text-sm font-semibold">
              Read more
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.75} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const WhySection = () => (
  <section data-testid="home-why" className="bg-[#09090B] text-white border-b border-zinc-800">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold mb-6">
            03 · Why OpExcellence
          </p>
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05]">
            Built for Asia-Pacific. <span className="text-zinc-500">Proven across it.</span>
          </h2>
          <p className="mt-8 text-zinc-400 leading-relaxed text-base lg:text-lg max-w-md">
            Our team has delivered on the ground in fourteen markets — from Hong Kong banks to
            Singapore hospitals to Australian healthcare networks. The same disciplined method,
            tuned to local context.
          </p>
        </div>

        <div className="lg:col-span-7 grid grid-cols-2 gap-8 lg:gap-10 content-start">
          {[
            "Practitioner-led delivery, never theory-only",
            "International body-of-knowledge alignment",
            "Hong Kong-headquartered, APAC-deployed",
            "Verified benefits — tracked into the P&L",
            "Bilingual delivery (English / 中文) on request",
            "Hands-on coaching after certification",
          ].map((item) => (
            <div key={item} className="flex items-start gap-3 border-t border-zinc-800 pt-6">
              <CheckCircle2 className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <span className="text-sm text-zinc-200 leading-relaxed">{item}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-8">
        {STATS.map((s) => (
          <Stat key={s.label} value={s.value} label={s.label} />
        ))}
      </div>
    </div>
  </section>
);

const TestimonialStrip = () => (
  <section data-testid="home-testimonials" className="border-b border-black/10">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
      <SectionHeader
        overline="04 · Voices"
        title={<>Outcomes our clients <span className="text-[#E63946]">talk about.</span></>}
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-black/10">
        {TESTIMONIALS.map((t, i) => (
          <figure
            key={i}
            data-testid={`testimonial-${i}`}
            className="bg-white p-10 lg:p-12 flex flex-col"
          >
            <span className="font-display text-6xl text-[#E63946] leading-none">"</span>
            <blockquote className="mt-6 font-display text-xl lg:text-2xl tracking-tight text-black leading-snug flex-1">
              {t.quote}
            </blockquote>
            <figcaption className="mt-10 pt-6 border-t border-black/10">
              <p className="text-sm font-semibold text-black">{t.name}</p>
              <p className="text-xs text-zinc-500 mt-1">{t.company}</p>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const ContactCTA = () => (
  <section data-testid="home-cta" className="bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
      <div className="border border-black p-10 lg:p-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
          <p className="text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold mb-6">
            Start the conversation
          </p>
          <h2 className="font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[1.02] text-black">
            Tell us where you want operations to be in 12 months.
          </h2>
          <p className="mt-6 text-zinc-600 max-w-xl text-base lg:text-lg leading-relaxed">
            Most engagements start with a 30-minute call. We'll listen, share what we've seen
            work in similar contexts, and tell you honestly whether we're the right team.
          </p>
        </div>
        <div className="lg:col-span-4 flex lg:justify-end">
          <Link
            to="/contact"
            data-testid="home-cta-contact"
            className="group inline-flex items-center gap-2 bg-black text-white px-8 py-5 text-sm font-semibold tracking-wide hover:bg-[#E63946] transition-colors"
          >
            Book a 30-min call
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.75} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => (
  <div data-testid="page-home">
    <Hero />
    <ClientsMarquee />
    <TrainingGrid />
    <ConsultingTiles />
    <WhySection />
    <TestimonialStrip />
    <ContactCTA />
  </div>
);

export default Home;
